import { useState, useEffect, useMemo, useRef } from "react";
import {
  normalizarTexto,
  apenasDigitos,
  formatarCNPJ,
  detectarCampo,
} from "./utils.js";
import "./style.css";

// Chave do sessionStorage usada para preservar o estado da busca
// (termo digitado + campo selecionado) quando o usuário navega para
// um formulário e volta. O sessionStorage sobrevive a remontagens do
// Dashboard (botão "Voltar" do navegador) mas é limpo ao fechar a
// aba, evitando "vazamento" entre sessões distintas.
const BUSCA_STORAGE_KEY = "companysearch:state:v1";

function carregarEstadoBuscaSalvo() {
  if (typeof window === "undefined") return { termo: "", campo: "todos" };
  try {
    const raw = window.sessionStorage.getItem(BUSCA_STORAGE_KEY);
    if (!raw) return { termo: "", campo: "todos" };
    const parsed = JSON.parse(raw);
    return {
      termo: typeof parsed?.termo === "string" ? parsed.termo : "",
      campo:
        typeof parsed?.campo === "string" && parsed.campo
          ? parsed.campo
          : "todos",
    };
  } catch {
    return { termo: "", campo: "todos" };
  }
}

const CAMPOS = [
  { id: "todos", rotulo: "Todos" },
  { id: "id", rotulo: "ID" },
  { id: "razaoSocial", rotulo: "Razão Social" },
  { id: "cnpj", rotulo: "CNPJ" },
  { id: "convenio", rotulo: "Convênio" },
];

// Rótulos curtos exibidos nos botões de "Abrir formulário" quando
// o resultado da busca retorna mais de um formulário vinculado à empresa.
const ROTULO_FORMULARIO = {
  tce: { titulo: "TCE", subtitulo: "Estágio" },
  tca: {
    titulo: "TCA Empregadora",
    subtitulo: "Aprendizagem (empresa empregadora)",
  },
  tcaEmpregador: {
    titulo: "TCA Empregador",
    subtitulo: "Aprendizagem (CIEEMG empregador)",
  },
};

// Mapeamento TIPOPROGRAMA (campo do JWT / empresa) → chave do único
// formulário permitido para essa empresa. Usado como defesa em
// profundidade: mesmo que o backend envie `possui: true` + `url` para
// formulários fora do TIPOPROGRAMA da empresa, o front só renderiza o
// que corresponde ao tipo correto.
//
//   TIPOPROGRAMA=0 → TCE (Estágio)
//   TIPOPROGRAMA=1 → TCA Empregadora (Aprendizagem empresa empregadora)
//   TIPOPROGRAMA=2 → TCA Empregador (Aprendizagem CIEEMG empregador)
const FORMULARIO_POR_TIPOPROGRAMA = {
  0: "tce",
  1: "tca",
  2: "tcaEmpregador",
};

/**
 * Filtra os formulários da empresa pelos permitidos pelo seu
 * TIPOPROGRAMA. Retorna um objeto com apenas a(s) chave(s) que
 * correspondem ao tipo de programa da empresa.
 *
 * - Sem TIPOPROGRAMA ou TIPOPROGRAMA não mapeado: retorna todos os
 *   formulários que o backend enviou (regra antiga — fallback seguro).
 * - TIPOPROGRAMA mapeado mas chave do formulário ausente no payload:
 *   retorna objeto vazio (nenhum botão).
 */
function filtrarFormulariosPorTipoPrograma(empresa) {
  const formularios = empresa?.formularios || {};
  const tipo =
    empresa?.TIPOPROGRAMA !== undefined && empresa?.TIPOPROGRAMA !== null
      ? String(empresa.TIPOPROGRAMA)
      : null;
  // Sem TIPOPROGRAMA: cai para a regra antiga (mostra todos com
  // possui === true + url) para não quebrar empresas antigas que
  // ainda não tenham esse campo populado.
  if (!tipo || !FORMULARIO_POR_TIPOPROGRAMA[tipo]) {
    return formularios;
  }
  const chavePermitida = FORMULARIO_POR_TIPOPROGRAMA[tipo];
  // Se o backend enviou o formulário permitido, devolve só ele;
  // caso contrário, devolve objeto vazio (nenhum botão).
  if (formularios[chavePermitida] !== undefined) {
    return { [chavePermitida]: formularios[chavePermitida] };
  }
  return {};
}

/**
 * Componente de busca de empresa — uso interno (colaboradores CIEE).
 *
 * Props novas (não quebram consumidores antigos):
 * - `resultados` (array): lista de empresas retornadas pelo backend.
 *   Cada item deve ter `{ id, razaoSocial|razaosocial, cnpj, formulario* }`.
 * - `onSelecionarFormulario` (fn): chamado com `{ url, empresa, formulario }`
 *   quando o usuário clica num botão OU quando há 1 resultado + 1 formulário
 *   (auto-navegação — Opção B). Use pra `navigate(url)`.
 * - `onErro` (fn): chamado com `string` quando o backend devolve erro/404.
 */
export default function BuscaEmpresa({
  onBuscar,
  onSelecionarFormulario,
  onErro,
  onTermoChange,
  placeholder = "Buscar empresa por ID, Razão Social, CNPJ ou Convênio",
  total = null,
  encontrados = null,
  carregando = false,
  resultados = null,
  erro = null,
  debounce = 400,
}) {
  const [termo, setTermo] = useState(() => carregarEstadoBuscaSalvo().termo);
  const [campo, setCampo] = useState(() => carregarEstadoBuscaSalvo().campo);
  const inputRef = useRef(null);

  // Notifica o pai (Dashboard) sempre que o termo digitado muda
  // para que ele esconda os cards de seleção de programa enquanto
  // há uma busca em andamento. Usa ref pra não depender da
  // identidade da callback (evita loop com debounce).
  const onTermoChangeRef = useRef(onTermoChange);
  useEffect(() => {
    onTermoChangeRef.current = onTermoChange;
  }, [onTermoChange]);
  useEffect(() => {
    onTermoChangeRef.current?.(termo);
  }, [termo]);

  // Persiste termo + campo no sessionStorage a cada alteração para que
  // o estado seja restaurado ao "Voltar" para esta página.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(
        BUSCA_STORAGE_KEY,
        JSON.stringify({ termo, campo }),
      );
    } catch {
      // sessionStorage indisponível (modo privado, etc.) — segue sem persistir.
    }
  }, [termo, campo]);

  // Guarda as callbacks mais recentes em refs para que o useEffect de
  // debounce NÃO dependa da identidade das funções — evita loop
  // quando o pai recria as callbacks a cada render (sem useCallback).
  const onBuscarRef = useRef(onBuscar);
  const onSelecionarFormularioRef = useRef(onSelecionarFormulario);
  useEffect(() => {
    onBuscarRef.current = onBuscar;
    onSelecionarFormularioRef.current = onSelecionarFormulario;
  }, [onBuscar, onSelecionarFormulario]);

  const aoDigitar = (e) => {
    const valor = e.target.value;
    const soNumeros = /^[\d.\-/]+$/.test(valor);
    const ehCNPJ =
      campo === "cnpj" ||
      (campo === "todos" && soNumeros && apenasDigitos(valor).length > 11);
    setTermo(ehCNPJ ? formatarCNPJ(valor) : valor);
  };

  const payload = useMemo(() => {
    const limpo = termo.trim();
    const campoFinal = campo === "todos" ? detectarCampo(limpo) : campo;
    const valor =
      campoFinal === "cnpj" || campoFinal === "id" || campoFinal === "convenio"
        ? apenasDigitos(limpo)
        : normalizarTexto(limpo);
    return {
      termo: limpo,
      campo: campoFinal,
      valor,
      automatico: campo === "todos",
    };
  }, [termo, campo]);

  useEffect(() => {
    const t = setTimeout(() => onBuscarRef.current?.(payload), debounce);
    return () => clearTimeout(t);
  }, [payload, debounce]);

  // Referência mantida por compatibilidade — usada no bloco de
  // "1 resultado + >1 formulário" abaixo para não renderizar o card
  // quando o resultado já foi consumido por outra renderização.
  // Auto-navegação DESATIVADA por decisão de UX: o usuário sempre
  // precisa clicar no botão do formulário para abrir, mesmo quando
  // há só 1 empresa + 1 formulário. Isso evita surpresas (abrir um
  // formulário sem o usuário pedir) e mantém o comportamento
  // previsível.
  const ultimoResultadoNavegadoRef = useRef(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // No-op intencional — auto-navegação removida.
  }, [resultados, carregando]);

  const limpar = () => {
    setTermo("");
    setCampo("todos");
    try {
      window.sessionStorage.removeItem(BUSCA_STORAGE_KEY);
    } catch {
      /* noop */
    }
    inputRef.current?.focus();
  };

  // Helpers de formatação do card de resultado
  const formatarCnpjExibicao = (cnpj) => {
    const d = apenasDigitos(cnpj || "");
    if (d.length !== 14) return cnpj || "";
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  };

  const rotuloStatus = (status) => {
    if (!status) return "";
    return status
      .toLowerCase()
      .split("_")
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(" ");
  };

  return (
    <div className="busca-empresa">
      <div className="busca-empresa__linha">
        <svg
          className="busca-empresa__icone"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>

        <input
          ref={inputRef}
          className="busca-empresa__input"
          value={termo}
          onChange={aoDigitar}
          onKeyDown={(e) => e.key === "Escape" && limpar()}
          placeholder={placeholder}
          inputMode={
            ["cnpj", "id", "convenio"].includes(campo) ? "numeric" : "text"
          }
          aria-label="Buscar empresa"
        />

        {carregando && (
          <span className="busca-empresa__spinner" aria-hidden="true" />
        )}

        {termo && !carregando && (
          <button
            className="busca-empresa__limpar"
            onClick={limpar}
            aria-label="Limpar busca"
          >
            ×
          </button>
        )}

        {encontrados !== null && total !== null && (
          <span className="busca-empresa__contador">
            {encontrados} de {total}
          </span>
        )}
      </div>

      <div
        className="busca-empresa__chips"
        role="group"
        aria-label="Buscar por"
      >
        {CAMPOS.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`busca-empresa__chip ${campo === c.id ? "busca-empresa__chip--ativo" : ""}`}
            onClick={() => setCampo(c.id)}
            aria-pressed={campo === c.id}
          >
            {c.rotulo}
          </button>
        ))}
      </div>

      {campo === "todos" && termo.trim() && (
        <p className="busca-empresa__dica">
          Buscando por{" "}
          <strong>{CAMPOS.find((c) => c.id === payload.campo)?.rotulo}</strong>
        </p>
      )}

      {/* Mensagem de erro */}
      {erro && !carregando && (
        <p className="busca-empresa__erro" role="alert">
          {erro}
        </p>
      )}

      {/* Resultados (lista visível quando > 1 empresa ou > 1 formulário) */}
      {!carregando && Array.isArray(resultados) && resultados.length > 1 && (
        <ul className="busca-empresa__resultados">
          {resultados.map((empresa) => {
            const razao =
              empresa.razaoSocial ||
              empresa.razaosocial ||
              "(sem razão social)";
            return (
              <li key={empresa.id} className="busca-empresa__resultado">
                <div className="busca-empresa__resultado-cabecalho">
                  <strong>{razao}</strong>
                  <span className="busca-empresa__resultado-meta">
                    {formatarCnpjExibicao(empresa.cnpj)}
                    {empresa.convenio && (
                      <>
                        {" "}
                        · Conv: <strong>{empresa.convenio}</strong>
                      </>
                    )}
                    {empresa.id && (
                      <>
                        {" "}
                        · ID: <strong>{empresa.id}</strong>
                      </>
                    )}
                  </span>
                </div>
                <div className="busca-empresa__resultado-botoes">
                  {Object.entries(
                    filtrarFormulariosPorTipoPrograma(empresa) || {},
                  ).map(([chave, f]) => {
                    // TRELLO — Empresa 529801 (TIPOPROGRAMA=0=TCE)
                    // tinha os 3 botões de formulário (TCE + TCA +
                    // TCA Empregador) aparecendo no card. Agora o
                    // backend só envia `url` para o tipo de
                    // formulário permitido pelo TIPOPROGRAMA, e o
                    // frontend reforça: só renderiza o botão se
                    // `possui === true` E `url` estiver definida.
                    //
                    // Quando o TIPOPROGRAMA permite o formulário
                    // mas ainda não existe registro vivo vinculado,
                    // o backend envia `possui: true` mas SEM
                    // `id`/`status` — nesse caso o botão aparece
                    // como "Novo" (empresa pode iniciar um).
                    if (!f || f.possui !== true || typeof f.url !== "string") {
                      return null;
                    }
                    const temRegistro = f.id !== undefined && f.id !== null;
                    const r = ROTULO_FORMULARIO[chave] || {
                      titulo: chave,
                      subtitulo: "",
                    };
                    const classeBtn = `busca-empresa__btn-form ${temRegistro ? "busca-empresa__btn-form--ativo" : "busca-empresa__btn-form--novo"}`;
                    return (
                      <button
                        key={chave}
                        type="button"
                        className={classeBtn}
                        onClick={() =>
                          onSelecionarFormulario?.({
                            url: f.url,
                            empresa,
                            formulario: { chave, ...f },
                          })
                        }
                        title={
                          temRegistro
                            ? `${r.titulo} #${f.id}`
                            : `Criar ${r.titulo} para esta empresa`
                        }
                      >
                        <span className="busca-empresa__btn-titulo">
                          {r.titulo}
                        </span>
                        <span className="busca-empresa__btn-subtitulo">
                          {temRegistro
                            ? `#${f.id} · ${rotuloStatus(f.status)}`
                            : "Novo"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Caso especial: 1 resultado + >1 formulário (não auto-navega) */}
      {!carregando &&
        Array.isArray(resultados) &&
        resultados.length === 1 &&
        resultados !== ultimoResultadoNavegadoRef.current &&
        (() => {
          const empresa = resultados[0];
          // TRELLO — Defesa em profundidade (mesma regra do
          // useEffect de auto-navegar e da lista de resultados):
          // exige `possui === true` E `url` definida para listar
          // o botão.
          // Auto-navegação DESATIVADA — agora mesmo com 1 resultado
          // + 1 formulário, exibimos o card com o botão para o
          // usuário clicar e abrir (decisão de UX).
          // Primeiro filtra pelo TIPOPROGRAMA, depois pela posse+url.
          const formularios = Object.entries(
            filtrarFormulariosPorTipoPrograma(empresa),
          ).filter(
            ([, f]) => f && f.possui === true && typeof f.url === "string",
          );
          if (formularios.length < 1) return null;
          const razao =
            empresa.razaoSocial || empresa.razaosocial || "(sem razão social)";
          return (
            <div className="busca-empresa__resultado busca-empresa__resultado--unico">
              <div className="busca-empresa__resultado-cabecalho">
                <strong>{razao}</strong>
                <span className="busca-empresa__resultado-meta">
                  {formatarCnpjExibicao(empresa.cnpj)}
                  {empresa.convenio && (
                    <>
                      {" "}
                      · Conv: <strong>{empresa.convenio}</strong>
                    </>
                  )}
                  {empresa.id && (
                    <>
                      {" "}
                      · ID: <strong>{empresa.id}</strong>
                    </>
                  )}
                </span>
              </div>
              <div className="busca-empresa__resultado-botoes">
                {formularios.map(([chave, f]) => {
                  // TRELLO — quando o TIPOPROGRAMA permite o
                  // formulário mas ainda não há registro vivo
                  // vinculado, o backend envia `possui: true` mas
                  // sem `id`/`status` — exibimos como "Novo".
                  const temRegistro = f.id !== undefined && f.id !== null;
                  const r = ROTULO_FORMULARIO[chave] || {
                    titulo: chave,
                    subtitulo: "",
                  };
                  const classeBtn = `busca-empresa__btn-form ${temRegistro ? "busca-empresa__btn-form--ativo" : "busca-empresa__btn-form--novo"}`;
                  return (
                    <button
                      key={chave}
                      type="button"
                      className={classeBtn}
                      onClick={() =>
                        onSelecionarFormulario?.({
                          url: f.url,
                          empresa,
                          formulario: { chave, ...f },
                        })
                      }
                      title={
                        temRegistro
                          ? `${r.titulo} #${f.id}`
                          : `Criar ${r.titulo} para esta empresa`
                      }
                    >
                      <span className="busca-empresa__btn-titulo">
                        {r.titulo}
                      </span>
                      <span className="busca-empresa__btn-subtitulo">
                        {temRegistro
                          ? `#${f.id} · ${rotuloStatus(f.status)}`
                          : "Novo"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}
    </div>
  );
}
